import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useCallback, ReactNode } from 'react';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useReturnFocusOnClose } from '@/hooks/useReturnFocusOnClose';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn } from '@/lib/utils';

export interface ResponsiveDialogProps {
    title: ReactNode;
    subtitle?: string;
    children: ReactNode;
    isOpen: boolean;
    onClose: () => void;
    footer?: ReactNode;
    showCloseButton?: boolean;
    forceModal?: boolean;
    className?: string;
    height?: string;
}

export const ResponsiveDialog = ({
    title,
    subtitle,
    children,
    isOpen,
    onClose,
    footer,
    showCloseButton = true,
    forceModal = false,
    className,
    height,
}: ResponsiveDialogProps) => {
    const isDesktop = useIsDesktop();

    // This dialog is opened from state, not a Radix DialogTrigger, so Radix can't
    // return focus to the trigger on close. Capture and restore it ourselves.
    const { captureTrigger, onCloseAutoFocus } = useReturnFocusOnClose();

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    if (isDesktop || forceModal) {
        return (
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent
                    className={cn(
                        'bg-secondary text-secondary-foreground border-none sm:max-w-[600px] flex flex-col p-0 gap-2 focus-visible:outline-none light:bg-card dark:bg-card light:text-foreground dark:text-foreground light:border light:border-[var(--neutral-grey-light)] dark:border dark:border-[var(--primary-light)]',
                        height ?? 'max-h-[80vh]',
                        className,
                    )}
                    showCloseButton={showCloseButton}
                    aria-modal="true"
                    onOpenAutoFocus={captureTrigger}
                    onCloseAutoFocus={onCloseAutoFocus}
                >
                    <div className="px-6 pt-6 pb-2">
                        <DialogHeader className="flex-shrink-0">
                            <DialogTitle className="type-subheading-medium">{title}</DialogTitle>
                        </DialogHeader>
                        {subtitle
                            ? (
                                    <DialogDescription className="text-sm text-secondary-foreground light:text-foreground dark:text-foreground flex-shrink-0 italic mt-2">{subtitle}</DialogDescription>
                                )
                            : (
                                    <DialogDescription className="sr-only">Dialog content</DialogDescription>
                                )}
                    </div>
                    <div className="flex-1 overflow-y-auto px-6">
                        {children}
                    </div>
                    <DialogFooter className="flex-shrink-0 p-0">
                        <div className="px-6 pb-6 w-full">
                            {footer}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent
                className={cn(
                    'bg-secondary-light border-none rounded-t-4xl flex flex-col p-0 focus-visible:outline-none light:bg-card dark:bg-card light:border light:border-[var(--neutral-grey-light)] dark:border dark:border-[var(--primary-light)]',
                    height ?? 'max-h-[80vh]',
                    className,
                )}
                side="bottom"
                showCloseButton={showCloseButton}
                aria-modal="true"
                onOpenAutoFocus={captureTrigger}
                onCloseAutoFocus={onCloseAutoFocus}
            >
                <SheetHeader className="flex flex-col gap-0 px-6 pt-6 pb-0 font-normal flex-shrink-0">
                    <SheetTitle className="text-secondary-foreground light:text-foreground dark:text-foreground">
                        <div className="type-subheading-medium">{title}</div>
                        <SheetClose className="hidden" />
                    </SheetTitle>
                    {subtitle && (
                        <div className="text-sm italic text-secondary-foreground light:text-foreground dark:text-foreground mt-2">
                            {subtitle}
                        </div>
                    )}
                </SheetHeader>
                <SheetDescription className="hidden" />
                <div className="flex flex-col text-white flex-1 overflow-hidden mt-[-20px] light:text-foreground dark:text-foreground">
                    <div className="flex flex-col font-normal gap-2 overflow-y-auto flex-1 px-6 pt-4">
                        {children}
                    </div>
                    <div className="flex-shrink-0 px-6 py-6">
                        {footer}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
